/**
 * Data shapes for inline dashboard charts (Sale overview, Total sale by category, …).
 * Replace or merge API payloads with {@link mergeSaleOverviewChartModel} / {@link mergeTotalSaleGroupedModel}.
 *
 * Graph ids match {@link DASHBOARD_GRAPH_OPTIONS} in ./dashboardGraphCatalog.js
 *
 * --- API labels / x-axis (categories) ---
 * All chart axis labels can come from the backend; keep numeric series aligned to the same length.
 * • Sale Overview: {@link SaleOverviewChartModel}.labels (+ amount / qty / customerCount).
 * • Total Sale (grouped bar): {@link TotalSaleGroupedModel}.categories (+ sale / exchange / refund).
 * • Sale by customer category: {@link SaleByCustomerCategoryModel}.categories + amounts (+ optional colors).
 * • Customer trend: {@link CustomerTrendModel}.labels + newCustomer + returnCustomer (parallel series).
 * • Top customers: {@link TopCustomersModel}.rows (ranked table: name, email, group, invoice, qty, amount).
 * • Customer group: {@link CustomerGroupModel}.tiers (Gold / Silver / Platinum counts for donut / bar / table).
 * • Age and gender: {@link AgeAndGenderModel}.rows (per age band: female, male, group, other — stacked bar + table).
 * • Saleperson performance: {@link SalespersonPerformanceModel}.names + sales + qty (bar Sales + line Qty, dual axis + table).
 * • Payment method: {@link PaymentMethodModel}.items (label, amount, color) — donut + bar + table; optional {@link PaymentMethodModel.totalSale}; {@link PaymentMethodModel.refundAmount} from `data.refund_amount`.
 * • Custom order: {@link CustomOrderChartModel}.categories + amount (THB) + qty + customer — grouped bar (THB thousands axis) + sortable table; {@link mapCustomOrderGraphApiToModelPatch} (`api/v1/POS/dashboard/custom-order-graph`).
 * • Repair order: same shape as {@link CustomOrderChartModel}; merged via {@link mergeRepairOrderModel} / {@link mapRepairOrderGraphApiToModelPatch} (`api/v1/POS/dashboard/repair-order-graph`).
 * • Refund: same shape as {@link CustomOrderChartModel}; merged via {@link mergeRefundModel} / {@link mapRefundGraphApiToModelPatch} (`api/v1/POS/dashboard/refund-graph`) (combo bar Amount + line Qty).
 * • Reserved: same shape; merged via {@link mergeReservedModel} / {@link mapReservedGraphApiToModelPatch} (`api/v1/POS/dashboard/reserved-graph`) (same card UI as Refund — row 7).
 * • Exchange / Wishlist: same shape; merged via {@link mergeExchangeModel} / {@link mergeWishlistModel} (row 8 — Refund-style chart + table); Exchange API {@link mapExchangeGraphApiToModelPatch} (`api/v1/POS/dashboard/exchange-graph`).
 * • Credit note: {@link CreditNoteChartModel} — donut + grouped bar (KPI strip) + sortable table; {@link mergeCreditNoteModel} / {@link mapCreditNoteGraphApiToModelPatch} (`api/v1/POS/dashboard/credit-note-graph`).
 * • Debit note: {@link DebitNoteChartModel} — Installment/Layby filter, donut + bar + sortable table; {@link mergeDebitNoteModel} / {@link mapDebitNoteGraphApiToModelPatch} (`api/v1/POS/dashboard/debit-note-graph`).
 * • Deposit: {@link DepositChartModel} — KPI strip + bar/line combo + table; {@link mergeDepositModel} / {@link mapDepositGraphApiToModelPatch} (`api/v1/POS/dashboard/deposit-graph`).
 * • Cash register (expected vs actual): {@link CashRegisterExpectedActualModel}; {@link mergeCashRegisterExpectedActualModel} / {@link mapCashRegisterGraphApiToModelPatch} (`api/v1/POS/dashboard/cash-register-graph`).
 * • KPI line modals (Total sales / Customer / Product): pass API label arrays when wired; defaults use
 *   `buildModalLineChartLabels` from `./dashboardModalChartLabels.js`.
 */

import moment from "moment";

export const DASHBOARD_CHART_GRAPH_IDS = {
  SALE_OVERVIEW: "sale_overview",
  TOTAL_SALE: "total_sale",
  TOP_PRODUCT: "top_product",
  ITEM_COLLECTION_SEGMENT: "item_collection_segment",
  SALE_BY_CUSTOMER_CATEGORY: "sale_by_customer_category",
  CUSTOMER_TREND: "customer_trend",
  TOP_CUSTOMER: "top_customer",
  CUSTOMER_GROUP: "customer_group",
  AGE_AND_GENDER: "age_and_gender",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `saleperson_performance`. */
  SALESPERSON_PERFORMANCE: "saleperson_performance",
  PAYMENT_METHOD: "payment_method",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `custom_order`. */
  CUSTOM_ORDER: "custom_order",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `repair_order`. */
  REPAIR_ORDER: "repair_order",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `refund`. */
  REFUND: "refund",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `reserve`. */
  RESERVE: "reserve",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `exchange`. */
  EXCHANGE: "exchange",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `wishlist`. */
  WISHLIST: "wishlist",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `credit_note`. */
  CREDIT_NOTE: "credit_note",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `debit_note`. */
  DEBIT_NOTE: "debit_note",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `deposit`. */
  DEPOSIT: "deposit",
  /** Matches {@link DASHBOARD_GRAPH_OPTIONS} id `cash_register`. */
  CASH_REGISTER: "cash_register",
};

/** Keys for dashboard per-chart loading state (not graph picker ids). */
export const DASHBOARD_CHART_LOAD_KEYS = {
  /** `segment-graph` API — Item/Collection segment card waits on this and {@link DASHBOARD_CHART_GRAPH_IDS.TOTAL_SALE}. */
  SEGMENT_GRAPH: "segment_graph",
  /** Single `customer-graphs` response feeds trend, group, and age/gender cards. */
  CUSTOMER_GRAPHS: "customer_graphs",
};

/** CDN medal art for customer tier badges (Top customers, Customer group). */
export const CUSTOMER_TIER_MEDAL_URL = {
  gold: "https://staticassets2.gis247.net/assets/v2/images/icons/gold-medal.png",
  platinum: "https://staticassets2.gis247.net/assets/v2/images/icons/platinum-medal.png",
  silver: "https://staticassets2.gis247.net/assets/v2/images/icons/silver-medal.png",
};

/**
 * @param {string | null | undefined} tierKey — `gold` | `silver` | `platinum` (case-insensitive)
 * @returns {string}
 */
export function customerTierMedalUrl(tierKey) {
  const k = String(tierKey ?? "gold").toLowerCase();
  if (k === "silver") return CUSTOMER_TIER_MEDAL_URL.silver;
  if (k === "platinum") return CUSTOMER_TIER_MEDAL_URL.platinum;
  return CUSTOMER_TIER_MEDAL_URL.gold;
}

/** Item / Collection segment donut — matches Figma `ConfigAndData-Pie` (amounts + percents). */
export const STATIC_SEGMENT_DONUT = {
  labels: ["Necklace", "Ring", "Bracelet", "Earring", "Pendant", "Others"],
  /** Demo collection per slice — same order as {@link STATIC_SEGMENT_DONUT.labels}. */
  collections: [
    "The Classic Collection",
    "Heritage Line",
    "The Classic Collection",
    "Daily Line",
    "Summer Drop",
    "—",
  ],
  values: [360000, 260000, 200000, 100000, 50000, 30000],
  colors: ["#5CC7C1", "#5C74C7", "#4E8EF0", "#F9BC44", "#F06547", "#434549"],
};

/** Single-series bar colors per category (Figma bar chart). Aligns with {@link STATIC_TOTAL_SALE_GROUPED}.categories length. */
export const STATIC_SEGMENT_BAR_COLORS = [
  "#5C74C7",
  "#4E8EF0",
  "#5CC7C1",
  "#F9BC44",
  "#434549",
  "#F06547",
  "#B2B3F5",
  "#2092EC",
  "#7367F0",
];

/**
 * @typedef {object} SaleOverviewChartModel
 * @property {string[]} labels — x-axis (DD/MM etc.); from API when provided, else derived from dashboard range ({@link saleOverviewModelFromDashboardRange}).
 * @property {number[]} amount — bar series (left axis)
 * @property {number[]} qty — line series (right axis)
 * @property {number[]} customerCount — line series (right axis)
 * @property {string} [leftAxisLabel]
 * @property {string} [rightAxisLabel]
 */

/**
 * @typedef {object} TotalSaleGroupedModel
 * @property {string[]} categories — x groups (e.g. product types / item); from API when provided, else empty via {@link EMPTY_TOTAL_SALE_GROUPED_MODEL}.
 * @property {string[]} [collections] — same length as categories when provided; collection name per row (segment table / tooltips).
 * @property {number[]} sale — navy bars (currency when `groupBy` is `amount`)
 * @property {number[]} exchange — teal bars
 * @property {number[]} refund — blue bars
 * @property {number[]} [saleQty] — unit counts for Sale series when `groupBy` is `quantity`
 * @property {number[]} [exchangeQty]
 * @property {number[]} [refundQty]
 * @property {number[]} [custom] — custom order / misc revenue per category (segment sales-type filter)
 * @property {number[]} [repair] — repair revenue per category
 * @property {number[]} [customQty]
 * @property {number[]} [repairQty]
 * @property {'amount' | 'quantity'} [groupBy] — for UI copy / future API switch
 */

/** Static demo — swap for API-mapped object of the same shape */
export const STATIC_SALE_OVERVIEW_CHART = {
  labels: ["01/09/24", "02/09/24", "03/09/24", "04/09/24", "05/09/24", "06/09/24", "07/09/24", "08/09/24", "09/09/24", "10/09/24", "11/09/24", "12/09/24"],
  amount: [88000, 82000, 135000, 118000, 105000, 98000, 152000, 62000, 142000, 115000, 78000, 85000],
  qty: [22, 28, 35, 30, 26, 24, 40, 18, 38, 32, 20, 24],
  customerCount: [18, 20, 32, 28, 24, 22, 36, 15, 34, 28, 17, 19],
  leftAxisLabel: "Amount",
  rightAxisLabel: "Value",
};

/**
 * After a failed or non-200 `total-sale-graph` response (explicit empty patch).
 * {@link mergeTotalSaleGroupedModel} already defaults to {@link EMPTY_TOTAL_SALE_GROUPED_MODEL}, not static demo data.
 * @type {Partial<TotalSaleGroupedModel>}
 */
export const EMPTY_TOTAL_SALE_GROUPED_PATCH = {
  categories: [],
  collections: [],
  sale: [],
  exchange: [],
  refund: [],
  saleQty: [],
  exchangeQty: [],
  refundQty: [],
};

/** Full {@link TotalSaleGroupedModel} for merge defaults — never use {@link STATIC_TOTAL_SALE_GROUPED} on the live dashboard. */
export const EMPTY_TOTAL_SALE_GROUPED_MODEL = {
  categories: [],
  collections: [],
  sale: [],
  exchange: [],
  refund: [],
  saleQty: [],
  exchangeQty: [],
  refundQty: [],
  custom: [],
  repair: [],
  customQty: [],
  repairQty: [],
  groupBy: /** @type {'amount' | 'quantity'} */ ("amount"),
};

/** Sale overview when there is no API data — empty series (not {@link STATIC_SALE_OVERVIEW_CHART}). */
export const EMPTY_SALE_OVERVIEW_CHART = {
  labels: [],
  amount: [],
  qty: [],
  customerCount: [],
  leftAxisLabel: "Amount",
  rightAxisLabel: "Value",
};

/** Custom / repair / refund / exchange / wishlist / reserved cards — empty shape for merge defaults. */
export const EMPTY_CUSTOM_ORDER_CHART = {
  categories: [],
  amount: [],
  qty: [],
  customer: [],
};

export const STATIC_TOTAL_SALE_GROUPED = {
  categories: ["Ring", "Bracelet", "Necklace", "Earring", "Doll", "Pendant", "Packaging", "Cleaning Kits", "Certification"],
  collections: [
    "Bridal",
    "The Classic Collection",
    "The Classic Collection",
    "Daily Line",
    "Kids Line",
    "Summer Drop",
    "Services",
    "Accessories",
    "Services",
  ],
  sale: [118000, 82000, 76000, 68000, 42000, 38000, 28000, 22000, 35000],
  exchange: [72000, 52000, 48000, 42000, 26000, 24000, 18000, 14000, 22000],
  refund: [42000, 32000, 28000, 24000, 16000, 14000, 12000, 9000, 15000],
  /** Demo unit counts when UI is "By Qty" — ranks differ from amount series (e.g. high-volume Packaging). */
  saleQty: [22, 15, 14, 24, 12, 9, 48, 19, 8],
  exchangeQty: [14, 10, 9, 11, 8, 7, 5, 4, 6],
  refundQty: [8, 6, 5, 5, 4, 3, 3, 2, 3],
  custom: [18000, 12000, 11000, 9600, 5200, 4800, 7500, 5200, 6800],
  repair: [9200, 6800, 5900, 5100, 3100, 2800, 4200, 3400, 4500],
  customQty: [3, 2, 2, 2, 1, 1, 2, 2, 2],
  repairQty: [2, 1, 2, 1, 1, 1, 1, 1, 2],
  groupBy: "amount",
};

/** Demo rows for Top Products table — replace with API mapping. */
export const STATIC_TOP_PRODUCTS_ROWS = [
  { sku: "NC001", name: "Classic Chain Necklace", item: "Necklace", collection: "The Classic Collection", qty: 24, qtySharePct: 12, amount: 520000 },
  { sku: "NC002", name: "Heritage Ring gold", item: "Ring", collection: "Heritage Line", qty: 18, qtySharePct: 9, amount: 490000 },
  { sku: "NC058", name: "Promise Pendant with diamond", item: "Necklace", collection: "The Classic Collection", qty: 22, qtySharePct: 11, amount: 470000 },
  { sku: "ER204", name: "Stud Earring pair", item: "Earring", collection: "Daily Line", qty: 30, qtySharePct: 8, amount: 460000 },
  { sku: "BR110", name: "Slim Bracelet", item: "Bracelet", collection: "The Classic Collection", qty: 15, qtySharePct: 7, amount: 450000 },
  { sku: "NC058", name: "Promise Pendant with diamond", item: "Necklace", collection: "The Classic Collection", qty: 20, qtySharePct: 10, amount: 440000 },
  { sku: "NC058", name: "Promise Pendant with diamond", item: "Necklace", collection: "The Classic Collection", qty: 19, qtySharePct: 10, amount: 430000 },
  { sku: "NC058", name: "Promise Pendant with diamond", item: "Necklace", collection: "The Classic Collection", qty: 18, qtySharePct: 10, amount: 400000 },
  { sku: "NC058", name: "Promise Pendant with diamond", item: "Necklace", collection: "The Classic Collection", qty: 17, qtySharePct: 10, amount: 390000 },
  { sku: "NC058", name: "Promise Pendant with diamond", item: "Necklace", collection: "The Classic Collection", qty: 16, qtySharePct: 10, amount: 370000 },
  { sku: "RG077", name: "Wedding Band pair", item: "Ring", collection: "Bridal", qty: 12, qtySharePct: 6, amount: 360000 },
  { sku: "NK303", name: "Layered Pendant set", item: "Necklace", collection: "Summer Drop", qty: 14, qtySharePct: 7, amount: 350000 },
];

/** Swapped mapping: New Customer -> amber, Return Customer -> teal. */
const SALE_BY_CUSTOMER_CATEGORY_DEFAULT_COLORS = ["#F9BC44", "#5CC7C1"];

/**
 * @typedef {object} SaleByCustomerCategoryModel
 * @property {string[]} categories — customer segment labels (parallel to {@link SaleByCustomerCategoryModel#amounts})
 * @property {number[]} amounts — sale amount per category (same length as categories)
 * @property {string[]} [colors] — optional hex per row
 */

/** Demo “New / Return customer” split — replace with API mapping. Percents are derived from amounts in the UI. */
export const STATIC_SALE_BY_CUSTOMER_CATEGORY = {
  categories: ["New Customer", "Return Customer"],
  amounts: [725000, 275000],
  colors: [...SALE_BY_CUSTOMER_CATEGORY_DEFAULT_COLORS],
};

/**
 * @param {Partial<SaleByCustomerCategoryModel> | null | undefined} patch
 * @returns {SaleByCustomerCategoryModel}
 */
export function mergeSaleByCustomerCategoryModel(patch) {
  const fallback = {
    categories: [],
    amounts: [],
    colors: [...SALE_BY_CUSTOMER_CATEGORY_DEFAULT_COLORS],
  };
  if (!patch || typeof patch !== "object") return fallback;
  const categories =
    Array.isArray(patch.categories) && patch.categories.length > 0 ? patch.categories.map((x) => String(x ?? "")) : fallback.categories;
  const len = categories.length;
  const alignNums = (arr, fb) => {
    const src = Array.isArray(arr)
      ? arr.map((x) => {
          const n = Number(x);
          return Number.isFinite(n) ? n : 0;
        })
      : [...fb];
    if (src.length >= len) return src.slice(0, len);
    const out = [...src];
    const pad = out.length > 0 ? out[out.length - 1] : 0;
    while (out.length < len) out.push(pad);
    return out;
  };
  const alignColors = (arr, fb) => {
    const src = Array.isArray(arr) && arr.length > 0 ? arr.map((x) => String(x)) : [...fb];
    if (src.length >= len) return src.slice(0, len);
    const out = [...src];
    while (out.length < len)
      out.push(SALE_BY_CUSTOMER_CATEGORY_DEFAULT_COLORS[out.length % SALE_BY_CUSTOMER_CATEGORY_DEFAULT_COLORS.length]);
    return out;
  };
  return {
    categories,
    amounts: alignNums(patch.amounts, fallback.amounts),
    colors: alignColors(patch.colors, fallback.colors),
  };
}

/**
 * Map `api/v1/POS/dashboard/customer-category` response to {@link SaleByCustomerCategoryModel} patch.
 * Uses the same category labels as {@link STATIC_SALE_BY_CUSTOMER_CATEGORY}. Percents in the UI are derived from amounts.
 *
 * @param {object | null | undefined} apiResponse — expects `data.new_customer` / `data.return_customer` with `amount` (and optional `percent`).
 * @returns {Partial<SaleByCustomerCategoryModel>}
 */
export function mapCustomerCategoryApiToModelPatch(apiResponse) {
  const d = apiResponse?.data ?? {};
  const nc = d?.new_customer ?? {};
  const rc = d?.return_customer ?? {};
  return {
    categories: ["New Customer", "Return Customer"],
    amounts: [toNum(nc.amount, 0), toNum(rc.amount, 0)],
  };
}

/**
 * @typedef {object} CustomerTrendModel
 * @property {string[]} labels — x-axis date labels (DD/MM/YY); from API when provided.
 * @property {number[]} newCustomer — count per label
 * @property {number[]} returnCustomer — count per label (same length as newCustomer)
 */

/** Demo daily new vs return customers — replace with API mapping. */
export const STATIC_CUSTOMER_TREND = {
  labels: STATIC_SALE_OVERVIEW_CHART.labels.slice(),
  newCustomer: [16, 14, 25, 26, 9, 5, 25, 30, 20, 15, 10, 45],
  returnCustomer: [6, 5, 9, 10, 12, 8, 15, 10, 8, 35, 6, 35],
};

/**
 * @param {Partial<CustomerTrendModel> | null | undefined} patch
 * @returns {CustomerTrendModel}
 */
export function mergeCustomerTrendModel(patch) {
  const fallback = {
    labels: [],
    newCustomer: [],
    returnCustomer: [],
  };
  if (!patch || typeof patch !== "object") return fallback;
  if (Array.isArray(patch.labels) && patch.labels.length === 0) {
    return { labels: [], newCustomer: [], returnCustomer: [] };
  }
  const labels =
    Array.isArray(patch.labels) && patch.labels.length > 0 ? patch.labels.map((x) => String(x ?? "")) : fallback.labels;
  const len = labels.length;
  const alignNums = (arr, fb) => {
    const src = Array.isArray(arr)
      ? arr.map((x) => {
          const n = Number(x);
          return Number.isFinite(n) ? n : 0;
        })
      : [...fb];
    if (src.length >= len) return src.slice(0, len);
    const out = [...src];
    const pad = out.length > 0 ? out[out.length - 1] : 0;
    while (out.length < len) out.push(pad);
    return out;
  };
  return {
    labels,
    newCustomer: alignNums(patch.newCustomer, fallback.newCustomer),
    returnCustomer: alignNums(patch.returnCustomer, fallback.returnCustomer),
  };
}

/**
 * @typedef {object} TopCustomerRow
 * @property {string} name
 * @property {string} email
 * @property {string} [avatarUrl] — optional; else initials on neutral tile
 * @property {string} [groupKey] — e.g. `gold` | `silver` | `platinum` (badge in Group column)
 * @property {number} invoiceCount
 * @property {number} qty
 * @property {number} amount
 */

/**
 * @typedef {object} TopCustomersModel
 * @property {TopCustomerRow[]} rows
 */

/** Demo ranked customers — replace with API mapping. */
export const STATIC_TOP_CUSTOMERS_ROWS = [
  { name: "Catherine Brown", email: "catherine123@gmail.com", groupKey: "gold", invoiceCount: 10, qty: 10, amount: 200000 },
  { name: "Jennifer Lee", email: "jennifer.lee@gmail.com", groupKey: "gold", invoiceCount: 8, qty: 8, amount: 140000 },
  { name: "Marie Williams", email: "marie_williams11@gmail.com", groupKey: "gold", invoiceCount: 5, qty: 5, amount: 140000 },
  { name: "Michael Harrison", email: "micheal.hrs@gmail.com", groupKey: "gold", invoiceCount: 5, qty: 5, amount: 100000 },
  { name: "Daniel Christopher", email: "daniel.christopher@gmail.com", groupKey: "gold", invoiceCount: 5, qty: 5, amount: 70000 },
  { name: "Sophie Turner", email: "sophie.turner@gmail.com", groupKey: "silver", invoiceCount: 4, qty: 12, amount: 68000 },
  { name: "James Wilson", email: "james.wilson@gmail.com", groupKey: "gold", invoiceCount: 4, qty: 6, amount: 65000 },
  { name: "Emily Davis", email: "emily.davis@gmail.com", groupKey: "silver", invoiceCount: 3, qty: 9, amount: 54000 },
  { name: "Robert Chen", email: "robert.chen@gmail.com", groupKey: "platinum", invoiceCount: 3, qty: 4, amount: 52000 },
  { name: "Lisa Anderson", email: "lisa.anderson@gmail.com", groupKey: "gold", invoiceCount: 3, qty: 7, amount: 48000 },
  { name: "David Park", email: "david.park@gmail.com", groupKey: "silver", invoiceCount: 2, qty: 5, amount: 42000 },
  { name: "Amanda Clark", email: "amanda.clark@gmail.com", groupKey: "gold", invoiceCount: 2, qty: 3, amount: 38000 },
];

/** @type {TopCustomersModel} */
export const STATIC_TOP_CUSTOMERS = {
  rows: STATIC_TOP_CUSTOMERS_ROWS.map((r) => ({ ...r })),
};

/**
 * @param {Partial<TopCustomersModel> | null | undefined} patch
 * @returns {TopCustomersModel}
 */
export function mergeTopCustomersModel(patch) {
  if (!patch || typeof patch !== "object") return { rows: [] };
  if (!Array.isArray(patch.rows)) return { rows: [] };
  if (patch.rows.length === 0) return { rows: [] };
  return {
    rows: patch.rows.map((r) => ({
      name: String(r?.name ?? ""),
      email: String(r?.email ?? ""),
      avatarUrl: r?.avatarUrl != null && String(r.avatarUrl).trim() !== "" ? String(r.avatarUrl) : undefined,
      groupKey: String(r?.groupKey ?? "gold"),
      invoiceCount: Number.isFinite(Number(r?.invoiceCount)) ? Number(r.invoiceCount) : 0,
      qty: Number.isFinite(Number(r?.qty)) ? Number(r.qty) : 0,
      amount: Number.isFinite(Number(r?.amount)) ? Number(r.amount) : 0,
    })),
  };
}

/**
 * Map `api/v1/POS/dashboard/top-customers` response to {@link TopCustomersModel} patch (`top_customers` array).
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<TopCustomersModel>}
 */
export function mapTopCustomersApiToModelPatch(apiResponse) {
  const list = Array.isArray(apiResponse?.top_customers) ? apiResponse.top_customers : [];
  return {
    rows: list.map((r) => {
      const g = String(r?.group ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
      const photo = r?.photo != null && String(r.photo).trim() !== "" ? String(r.photo).trim() : undefined;
      return {
        name: String(r?.name ?? ""),
        email: String(r?.email ?? ""),
        avatarUrl: photo,
        groupKey: g || "gold",
        invoiceCount: toNum(r?.invoice, 0),
        qty: toNum(r?.qty, 0),
        amount: toNum(r?.amount, 0),
      };
    }),
  };
}

/**
 * @typedef {object} CustomerGroupTier
 * @property {string} id — e.g. `gold` | `silver` | `platinum` | `diamond` (from API)
 * @property {string} legendLabel — footer legend (Figma uses lowercase for platinum)
 * @property {string} tableLabel — first table column
 * @property {string} barLabel — x-axis category (typically Title Case)
 * @property {number} count
 * @property {string} color — slice / bar fill
 */

/**
 * @typedef {object} CustomerGroupModel
 * @property {CustomerGroupTier[]} tiers — gold / silver / platinum in fixed order when only those three; otherwise API order (e.g. + diamond)
 */

const CUSTOMER_GROUP_TIER_ORDER = /** @type {const} */ (["gold", "silver", "platinum"]);
const CUSTOMER_GROUP_CLASSIC_IDS = new Set(CUSTOMER_GROUP_TIER_ORDER);
const CUSTOMER_GROUP_DYNAMIC_COLORS = ["#F9C54A", "#BEBEBE", "#ACB6D2", "#5CC7C1", "#4E8EF0", "#F06547", "#7367F0"];

/** Demo tier counts (300 total) — replace with API mapping. */
export const STATIC_CUSTOMER_GROUP = {
  tiers: [
    { id: "gold", legendLabel: "Gold", tableLabel: "Gold", barLabel: "Gold", count: 150, color: "#F9C54A" },
    { id: "silver", legendLabel: "Silver", tableLabel: "Silver", barLabel: "Silver", count: 75, color: "#ACB6D2" },
    { id: "platinum", legendLabel: "platinum", tableLabel: "platinum", barLabel: "Platinum", count: 75, color: "#BEBEBE" },
  ],
};

/**
 * @param {Partial<CustomerGroupModel> | null | undefined} patch
 * @returns {CustomerGroupModel}
 */
export function mergeCustomerGroupModel(patch) {
  const baseTiers = STATIC_CUSTOMER_GROUP.tiers.map((t) => ({ ...t }));
  if (!patch || typeof patch !== "object" || !Array.isArray(patch.tiers)) {
    return { tiers: [] };
  }
  if (patch.tiers.length === 0) {
    return { tiers: [] };
  }

  const normalized = patch.tiers.map((t, idx) => {
    const rawId = String(t?.id ?? t?.key ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    const id = rawId || `tier_${idx}`;
    const b = baseTiers.find((x) => x.id === id) ?? baseTiers[Math.min(idx, baseTiers.length - 1)];
    const count = Number(t.count);
    const c = Number.isFinite(count) ? count : 0;
    const col =
      typeof t.color === "string" && t.color.trim() !== ""
        ? t.color.trim()
        : CUSTOMER_GROUP_CLASSIC_IDS.has(id)
          ? b.color
          : CUSTOMER_GROUP_DYNAMIC_COLORS[idx % CUSTOMER_GROUP_DYNAMIC_COLORS.length];
    return {
      id,
      legendLabel: String(t.legendLabel ?? t.label ?? b.legendLabel),
      tableLabel: String(t.tableLabel ?? t.legendLabel ?? t.label ?? b.tableLabel),
      barLabel: String(t.barLabel ?? t.legendLabel ?? t.label ?? b.barLabel),
      count: c,
      color: col,
    };
  });

  const onlyClassic =
    normalized.length <= 3 && normalized.length > 0 && normalized.every((t) => CUSTOMER_GROUP_CLASSIC_IDS.has(t.id));

  if (onlyClassic) {
    const byId = new Map(normalized.map((t) => [t.id, t]));
    return {
      tiers: CUSTOMER_GROUP_TIER_ORDER.map((id, idx) => {
        const p = byId.get(id);
        const b = baseTiers[idx];
        if (!p || typeof p !== "object") {
          return { ...b, count: 0 };
        }
        return {
          id,
          legendLabel: p.legendLabel,
          tableLabel: p.tableLabel,
          barLabel: p.barLabel,
          count: p.count,
          color: p.color,
        };
      }),
    };
  }

  return { tiers: normalized };
}

/**
 * @typedef {object} AgeGenderRow
 * @property {string} ageLabel — e.g. `7-22 Yrs`
 * @property {number} female
 * @property {number} male
 * @property {number} group
 * @property {number} other
 */

/**
 * @typedef {object} AgeAndGenderModel
 * @property {AgeGenderRow[]} rows
 */

/** Demo rows — replace with API mapping (table + stacked-bar share the same counts). */
export const STATIC_AGE_GENDER = {
  rows: [
    { ageLabel: "7-22 Yrs", female: 16, male: 16, group: 16, other: 6 },
    { ageLabel: "23-38 Yrs", female: 14, male: 14, group: 14, other: 5 },
    { ageLabel: "39-54 Yrs", female: 25, male: 25, group: 25, other: 9 },
    { ageLabel: "55+ Yrs", female: 25, male: 25, group: 25, other: 9 },
  ],
};

/**
 * @param {Partial<AgeAndGenderModel> | null | undefined} patch
 * @returns {AgeAndGenderModel}
 */
export function mergeAgeAndGenderModel(patch) {
  if (!patch || typeof patch !== "object") {
    return { rows: [] };
  }
  if (!Array.isArray(patch.rows)) {
    return { rows: [] };
  }
  if (patch.rows.length === 0) {
    return { rows: [] };
  }
  const defaultRow = { ageLabel: "", female: 0, male: 0, group: 0, other: 0 };
  return {
    rows: patch.rows.map((r) => {
      const b = defaultRow;
      const num = (x) => {
        const n = Number(x);
        return Number.isFinite(n) ? n : 0;
      };
      return {
        ageLabel: String(r?.ageLabel ?? b.ageLabel),
        female: num(r?.female ?? b.female),
        male: num(r?.male ?? b.male),
        group: num(r?.group ?? b.group),
        other: num(r?.other ?? r?.others ?? b.other),
      };
    }),
  };
}

/**
 * Map `api/v1/POS/dashboard/customer-graphs` `data` into patches for
 * {@link CustomerTrendModel}, {@link CustomerGroupModel}, and {@link AgeAndGenderModel}.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {{
 *   customerTrendPartial: Partial<CustomerTrendModel>;
 *   customerGroupPartial: Partial<CustomerGroupModel>;
 *   ageAndGenderPartial: Partial<AgeAndGenderModel>;
 * }}
 */
export function mapCustomerGraphsApiResponse(apiResponse) {
  const d = apiResponse?.data ?? {};
  const trendList = Array.isArray(d.customer_trend) ? d.customer_trend : [];
  const groupList = Array.isArray(d.customer_group) ? d.customer_group : [];
  const ageList = Array.isArray(d.age_gender) ? d.age_gender : [];

  return {
    customerTrendPartial: {
      labels: trendList.map((p) => String(p?.date ?? "")),
      newCustomer: trendList.map((p) => toNum(p?.new_customer, 0)),
      returnCustomer: trendList.map((p) => toNum(p?.return_customer, 0)),
    },
    customerGroupPartial: {
      tiers: groupList.map((g, i) => {
        const label = String(g?.group_name ?? "");
        const slug = label.trim().toLowerCase().replace(/\s+/g, "_") || `tier_${i}`;
        return {
          id: slug,
          key: slug,
          legendLabel: label,
          tableLabel: label,
          barLabel: label,
          count: toNum(g?.count, 0),
          color: CUSTOMER_GROUP_DYNAMIC_COLORS[i % CUSTOMER_GROUP_DYNAMIC_COLORS.length],
        };
      }),
    },
    ageAndGenderPartial: {
      rows: ageList.map((r) => ({
        ageLabel: String(r?.age_range ?? ""),
        female: toNum(r?.female, 0),
        male: toNum(r?.male, 0),
        group: toNum(r?.group, 0),
        other: toNum(r?.others ?? r?.other, 0),
      })),
    },
  };
}

/**
 * @typedef {object} SalespersonPerformanceModel
 * @property {string[]} names — x categories (salesperson labels)
 * @property {number[]} sales — currency amount per person (bar → right axis in UI)
 * @property {number[]} qty — quantity per person (line → left axis in UI)
 */

/** Demo series (Figma table + combo chart) — replace with API mapping. */
export const STATIC_SALESPERSON_PERFORMANCE = {
  names: [
    "Alice Smith",
    "Bob Johnson",
    "Carol Davis",
    "David Brown",
    "Eva White",
    "Frank Miller",
    "Henry Wilson",
    "Iris Martinez",
    "Jack Thompson",
    "Grace Lee",
  ],
  sales: [200000, 180000, 160000, 150000, 130000, 120000, 110000, 95000, 90000, 75000],
  qty: [50, 40, 35, 30, 20, 25, 22, 18, 15, 10],
};

/** Live dashboard — no API data (not {@link STATIC_SALESPERSON_PERFORMANCE}). */
export const EMPTY_SALESPERSON_PERFORMANCE = {
  names: [],
  sales: [],
  qty: [],
};

/**
 * @param {Partial<SalespersonPerformanceModel> | null | undefined} patch
 * @param {SalespersonPerformanceModel} [base=EMPTY_SALESPERSON_PERFORMANCE]
 * @returns {SalespersonPerformanceModel}
 */
export function mergeSalespersonPerformanceModel(patch, base = EMPTY_SALESPERSON_PERFORMANCE) {
  const fallback = {
    names: [...base.names],
    sales: [...base.sales],
    qty: [...base.qty],
  };
  if (!patch || typeof patch !== "object") return fallback;
  if (Array.isArray(patch.names) && patch.names.length === 0) {
    return { names: [], sales: [], qty: [] };
  }
  const names =
    Array.isArray(patch.names) && patch.names.length > 0
      ? patch.names.map((x) => (x == null ? "" : String(x)))
      : fallback.names;
  const len = names.length;
  const alignNum = (arr, fb) => {
    const src = Array.isArray(arr) ? arr.map((x) => Number(x)) : [];
    const pad = Array.isArray(fb) ? fb : [];
    const out = [];
    for (let i = 0; i < len; i += 1) {
      const v = src[i];
      const fbI = pad[Math.min(i, Math.max(0, pad.length - 1))];
      out.push(Number.isFinite(v) ? v : Number(fbI) || 0);
    }
    return out;
  };
  return {
    names,
    sales: alignNum(patch.sales, fallback.sales),
    qty: alignNum(patch.qty, fallback.qty),
  };
}

/**
 * Map `api/v1/POS/dashboard/salesperson-graph` response to {@link SalespersonPerformanceModel} patch.
 *
 * @param {object | null | undefined} apiResponse — expects `data[]` with `salesperson_name`, `sales`, `qty`.
 * @returns {Partial<SalespersonPerformanceModel>}
 */
export function mapSalespersonGraphApiToModelPatch(apiResponse) {
  const rows = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
  return {
    names: rows.map((r) => String(r?.salesperson_name ?? "")),
    sales: rows.map((r) => toNum(r?.sales, 0)),
    qty: rows.map((r) => toNum(r?.qty, 0)),
  };
}

/**
 * @typedef {object} PaymentMethodItem
 * @property {string} id
 * @property {string} label — display / defaultValue for i18n
 * @property {string} [labelKey] — optional i18n key
 * @property {number} amount
 * @property {string} color — hex fill for slice / bar / legend
 */

/**
 * @typedef {object} PaymentMethodModel
 * @property {PaymentMethodItem[]} items
 * @property {number} [totalSale] — optional center/summary total; defaults to sum of amounts
 * @property {number} [refundAmount] — cash refund total (not a donut slice); from graph API `refund_amount`
 */

/** Demo payment split (Figma) — replace with API mapping. */
export const STATIC_PAYMENT_METHOD = {
  items: [
    { id: "cash", label: "Cash", amount: 100000, color: "#5C74C7" },
    { id: "bank", label: "Bank", amount: 500000, color: "#5CC7C1" },
    { id: "credit_card", label: "Credit card", amount: 400000, color: "#F9BC44" },
    { id: "credit_note", label: "Credit note", amount: 0, color: "#4E8EF0" },
  ],
  totalSale: 1000000,
  refundAmount: 0,
};

/** Live dashboard — no API data (not {@link STATIC_PAYMENT_METHOD}). */
export const EMPTY_PAYMENT_METHOD_MODEL = {
  items: STATIC_PAYMENT_METHOD.items.map((x) => ({ ...x, amount: 0 })),
  totalSale: 0,
  refundAmount: 0,
};

/**
 * @param {Partial<PaymentMethodModel> | null | undefined} patch
 * @param {PaymentMethodModel} [base=EMPTY_PAYMENT_METHOD_MODEL]
 * @returns {PaymentMethodModel}
 */
export function mergePaymentMethodModel(patch, base = EMPTY_PAYMENT_METHOD_MODEL) {
  const fbItems = base.items.map((x) => ({ ...x }));
  const sumAmounts = (arr) =>
    arr.reduce((s, x) => s + (Number.isFinite(Number(x.amount)) ? Number(x.amount) : 0), 0);
  const baseTotal =
    typeof base.totalSale === "number" && Number.isFinite(base.totalSale) ? base.totalSale : sumAmounts(fbItems);
  const baseRefund = Number(base.refundAmount);
  const pickRefund = (p) => {
    if (!p || typeof p !== "object" || p.refundAmount == null) return Number.isFinite(baseRefund) ? baseRefund : 0;
    const r = Number(p.refundAmount);
    return Number.isFinite(r) ? r : Number.isFinite(baseRefund) ? baseRefund : 0;
  };

  if (!patch || typeof patch !== "object") {
    return { items: fbItems, totalSale: baseTotal, refundAmount: pickRefund(base) };
  }

  const byId = new Map(
    (Array.isArray(patch.items) ? patch.items : []).map((it) => [String(it?.id ?? ""), it]),
  );
  const items = fbItems.map((b) => {
    const p = byId.get(b.id);
    if (!p || typeof p !== "object") return { ...b };
    const amt = Number(p.amount);
    return {
      ...b,
      label: String(p.label ?? b.label),
      labelKey: typeof p.labelKey === "string" ? p.labelKey : b.labelKey,
      amount: Number.isFinite(amt) ? amt : b.amount,
      color: typeof p.color === "string" && p.color.trim() !== "" ? p.color.trim() : b.color,
    };
  });

  const ts = Number(patch.totalSale);
  const itemSum = sumAmounts(items);
  const totalSale =
    patch.totalSale != null && Number.isFinite(ts) ? ts : itemSum > 0 ? itemSum : baseTotal;
  return { items, totalSale, refundAmount: pickRefund(patch) };
}

/**
 * Map `api/v1/POS/dashboard/payment-method-graph` response to {@link PaymentMethodModel} patch.
 *
 * @param {object | null | undefined} apiResponse — expects `data.total`, `refund_amount`, and keys: `cash`, `bank`, `credit_card`, `credit_note`.
 * @returns {Partial<PaymentMethodModel>}
 */
export function mapPaymentMethodGraphApiToModelPatch(apiResponse) {
  const d = apiResponse?.data ?? {};
  return {
    totalSale: toNum(d?.total, 0),
    refundAmount: toNum(d?.refund_amount, 0),
    items: [
      { id: "cash", amount: toNum(d?.cash?.amount, 0) },
      { id: "bank", amount: toNum(d?.bank?.amount, 0) },
      { id: "credit_card", amount: toNum(d?.credit_card?.amount, 0) },
      { id: "credit_note", amount: toNum(d?.credit_note?.amount, 0) },
    ],
  };
}

/**
 * @typedef {object} CustomOrderChartModel
 * @property {string[]} categories — product / item labels (x-axis)
 * @property {number[]} amount — sale amount per category in **THB** (chart uses thousands vs same-axis qty & customer counts)
 * @property {number[]} qty
 * @property {number[]} customer — distinct customer count (or volume per design / API)
 */

/** Demo grouped metrics (Figma Custom Order) — replace with API mapping. */
export const STATIC_CUSTOM_ORDER = {
  categories: STATIC_TOTAL_SALE_GROUPED.categories.slice(),
  amount: [118000, 92000, 86000, 78000, 52000, 103000, 32000, 26000, 54000],
  qty: [38, 32, 28, 26, 16, 60, 18, 14, 22],
  customer: [28, 24, 22, 20, 12, 45, 14, 11, 18],
};

/**
 * @param {Partial<CustomOrderChartModel> | null | undefined} patch
 * @param {CustomOrderChartModel} [base=EMPTY_CUSTOM_ORDER_CHART]
 * @returns {CustomOrderChartModel}
 */
export function mergeCustomOrderModel(patch, base = EMPTY_CUSTOM_ORDER_CHART) {
  const fallback = {
    categories: [...base.categories],
    amount: [...base.amount],
    qty: [...base.qty],
    customer: [...base.customer],
  };
  if (!patch || typeof patch !== "object") return fallback;

  if (Array.isArray(patch.categories) && patch.categories.length === 0) {
    return { categories: [], amount: [], qty: [], customer: [] };
  }

  const categories =
    Array.isArray(patch.categories) && patch.categories.length > 0
      ? patch.categories.map((x) => (x == null ? "" : String(x)))
      : fallback.categories;
  const n = categories.length;
  const align = (arr, fb) => {
    const src = Array.isArray(arr) ? arr : [];
    const pad = Array.isArray(fb) ? fb : [];
    const out = [];
    for (let i = 0; i < n; i += 1) {
      const v = Number(src[i]);
      const fbI = pad[Math.min(i, Math.max(0, pad.length - 1))];
      out.push(Number.isFinite(v) ? v : Number(fbI) || 0);
    }
    return out;
  };
  return {
    categories,
    amount: align(patch.amount, fallback.amount),
    qty: align(patch.qty, fallback.qty),
    customer: align(patch.customer, fallback.customer),
  };
}

/** Demo repair-order metrics (Figma Repair Order card) — same shape as custom order. */
export const STATIC_REPAIR_ORDER = {
  categories: STATIC_CUSTOM_ORDER.categories.slice(),
  amount: [88000, 62000, 58000, 52000, 32000, 103000, 26000, 20000, 42000],
  qty: [28, 24, 22, 20, 12, 60, 14, 10, 18],
  customer: [22, 18, 17, 15, 9, 45, 11, 9, 14],
};

/**
 * @param {Partial<CustomOrderChartModel> | null | undefined} patch
 * @returns {CustomOrderChartModel}
 */
export function mergeRepairOrderModel(patch) {
  return mergeCustomOrderModel(patch, EMPTY_CUSTOM_ORDER_CHART);
}

/** Demo refund metrics (Figma Refund card — category axis, Amount bars + Qty line) — same row shape as custom order. */
export const STATIC_REFUND = {
  categories: [
    "Bracelet",
    "Cleaning Kits",
    "Certification",
    "Doll",
    "Earrings",
    "Necklace",
    "Pandent",
    "Packaging",
    "Ring",
  ],
  amount: [50000, 15000, 45000, 35000, 45000, 40000, 20000, 30000, 35000],
  qty: [2, 1, 1, 1, 2, 1, 1, 1, 1],
  customer: [2, 1, 1, 1, 2, 1, 1, 1, 1],
};

/**
 * @param {Partial<CustomOrderChartModel> | null | undefined} patch
 * @returns {CustomOrderChartModel}
 */
export function mergeRefundModel(patch) {
  return mergeCustomOrderModel(patch, EMPTY_CUSTOM_ORDER_CHART);
}

/** Demo reserved (Figma — same dual-axis chart + table as Refund; nine categories on chart). */
export const STATIC_RESERVED = {
  categories: STATIC_REFUND.categories.slice(),
  amount: [50000, 15000, 45000, 35000, 45000, 40000, 20000, 30000, 35000],
  qty: [2, 1, 1, 1, 2, 1, 1, 1, 1],
  customer: [2, 1, 1, 1, 2, 1, 1, 1, 1],
};

/**
 * @param {Partial<CustomOrderChartModel> | null | undefined} patch
 * @returns {CustomOrderChartModel}
 */
export function mergeReservedModel(patch) {
  return mergeCustomOrderModel(patch, EMPTY_CUSTOM_ORDER_CHART);
}

/** X-labels for Exchange / Wishlist (Figma — Pendant spelling). */
export const STATIC_EXCHANGE_WISHLIST_CATEGORIES = [
  "Bracelet",
  "Cleaning Kits",
  "Certification",
  "Doll",
  "Earrings",
  "Necklace",
  "Pendant",
  "Packaging",
  "Ring",
];

/** Demo exchange — dual-axis combo matching Figma row 7 left card. */
export const STATIC_EXCHANGE = {
  categories: STATIC_EXCHANGE_WISHLIST_CATEGORIES.slice(),
  amount: [52000, 18000, 49000, 12000, 51000, 26000, 23000, 20000, 24000],
  qty: [2, 1, 2, 1, 2, 1, 1, 1, 1],
  customer: [2, 1, 2, 1, 2, 1, 1, 1, 1],
};

/** Demo wishlist — same chart shape as Exchange (Figma shows matching series). */
export const STATIC_WISHLIST = {
  categories: STATIC_EXCHANGE_WISHLIST_CATEGORIES.slice(),
  amount: [...STATIC_EXCHANGE.amount],
  qty: [...STATIC_EXCHANGE.qty],
  customer: [...STATIC_EXCHANGE.customer],
};

/**
 * @param {Partial<CustomOrderChartModel> | null | undefined} patch
 * @returns {CustomOrderChartModel}
 */
export function mergeExchangeModel(patch) {
  return mergeCustomOrderModel(patch, EMPTY_CUSTOM_ORDER_CHART);
}

/**
 * @param {Partial<CustomOrderChartModel> | null | undefined} patch
 * @returns {CustomOrderChartModel}
 */
export function mergeWishlistModel(patch) {
  if (patch && Array.isArray(patch.categories) && patch.categories.length === 0) {
    return { categories: [], amount: [], qty: [], customer: [] };
  }
  return mergeCustomOrderModel(patch, EMPTY_CUSTOM_ORDER_CHART);
}

/**
 * @typedef {object} DepositChartRow
 * @property {string} date — axis / table label (e.g. `DD/MM/YY`)
 * @property {number} transaction
 * @property {number} amount
 */

/**
 * @typedef {object} DepositChartModel
 * @property {DepositChartRow[]} rows
 * @property {number} totalAmount
 * @property {number} totalTransactions
 * @property {number} avgAmount
 */

const STATIC_DEPOSIT_DEMO_ROWS = [
  { date: "01/09/24", transaction: 2, amount: 50000 },
  { date: "02/09/24", transaction: 1, amount: 35000 },
  { date: "03/09/24", transaction: 2, amount: 45000 },
  { date: "04/09/24", transaction: 1, amount: 15000 },
  { date: "05/09/24", transaction: 1, amount: 45000 },
  { date: "06/09/24", transaction: 1, amount: 40000 },
  { date: "07/09/24", transaction: 1, amount: 20000 },
  { date: "08/09/24", transaction: 1, amount: 30000 },
  { date: "09/09/24", transaction: 1, amount: 35000 },
  { date: "10/09/24", transaction: 1, amount: 25000 },
  { date: "11/09/24", transaction: 1, amount: 25000 },
  { date: "12/09/24", transaction: 1, amount: 35000 },
];

/** Demo deposit — Figma / storybook only; dashboard uses {@link EMPTY_DEPOSIT_MODEL} when there is no API data. */
export const STATIC_DEPOSIT = (() => {
  const rows = STATIC_DEPOSIT_DEMO_ROWS.map((r) => ({ ...r }));
  const totalAmount = rows.reduce((s, r) => s + r.amount, 0);
  const totalTransactions = rows.reduce((s, r) => s + r.transaction, 0);
  const avgAmount = totalTransactions > 0 ? totalAmount / totalTransactions : 0;
  return { rows, totalAmount, totalTransactions, avgAmount };
})();

/** Empty deposit card — no API data or explicit empty `rows` from API. */
export const EMPTY_DEPOSIT_MODEL = {
  rows: [],
  totalAmount: 0,
  totalTransactions: 0,
  avgAmount: 0,
};

/**
 * @param {Partial<DepositChartModel> | null | undefined} patch
 * @returns {DepositChartModel}
 */
export function mergeDepositModel(patch) {
  if (!patch || typeof patch !== "object") {
    return { rows: [], totalAmount: 0, totalTransactions: 0, avgAmount: 0 };
  }
  const rows = Array.isArray(patch.rows)
    ? patch.rows.map((r) => ({
        date: String(r?.date ?? ""),
        transaction: Math.round(Number(r?.transaction) || 0),
        amount: Number.isFinite(Number(r?.amount)) ? Number(r.amount) : 0,
      }))
    : [];
  const ta = Number(patch.totalAmount);
  const tt = Number(patch.totalTransactions);
  const av = Number(patch.avgAmount);
  let totalAmount = Number.isFinite(ta) ? ta : rows.reduce((s, r) => s + r.amount, 0);
  let totalTransactions = Number.isFinite(tt) ? tt : rows.reduce((s, r) => s + r.transaction, 0);
  let avgAmount = Number.isFinite(av) ? av : totalTransactions > 0 ? totalAmount / totalTransactions : 0;
  return { rows, totalAmount, totalTransactions, avgAmount };
}

/**
 * Map `api/v1/POS/dashboard/deposit-graph` response to {@link DepositChartModel} patch.
 * Expects `data.summary` (`total_amount`, `total_transactions`, `avg`) and `data.points[]` with `x`, `amount`, `transactions`.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<DepositChartModel>}
 */
export function mapDepositGraphApiToModelPatch(apiResponse) {
  const num = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const d = apiResponse?.data ?? {};
  const s = d?.summary ?? {};
  const totalAmount = num(s?.total_amount, 0);
  const totalTransactions = num(s?.total_transactions, 0);
  const avgFromApi = num(s?.avg, NaN);
  const avgAmount = Number.isFinite(avgFromApi)
    ? avgFromApi
    : totalTransactions > 0
      ? totalAmount / totalTransactions
      : 0;

  const rawPoints = Array.isArray(d?.points) ? d.points : [];
  const parsed = rawPoints
    .map((row) => {
      const dateStr = String(row?.x ?? row?.date ?? "").trim();
      const m = moment(
        dateStr,
        ["DD/MM/YYYY", "D/M/YYYY", "DD/MM/YY", "D/M/YY", "YYYY-MM-DD"],
        true,
      );
      return {
        dateStr,
        m: m.isValid() ? m : null,
        transaction: Math.round(num(row?.transactions ?? row?.transaction, 0)),
        amount: num(row?.amount, 0),
      };
    })
    .filter((x) => x.dateStr);

  parsed.sort((a, b) => {
    if (a.m && b.m) return a.m.valueOf() - b.m.valueOf();
    if (a.m) return -1;
    if (b.m) return 1;
    return String(a.dateStr).localeCompare(String(b.dateStr));
  });

  const rows = parsed.map((x) => ({
    date: x.dateStr,
    transaction: x.transaction,
    amount: x.amount,
  }));

  return {
    rows,
    totalAmount,
    totalTransactions,
    avgAmount,
  };
}

/**
 * @typedef {object} CreditNoteSummary
 * @property {number} totalAmount — donut center + KPI "Total amount"
 * @property {number} creditNotePlus — KPI teal series total
 * @property {number} redeemedMinus — KPI orange series total (display magnitude)
 * @property {number} changes — net KPI (credit − redeemed over period)
 */

/**
 * @typedef {object} CreditNoteDonutSlice
 * @property {number} creditPlus
 * @property {number} redeemedMinus
 */

/**
 * @typedef {object} CreditNoteTableRow
 * @property {string} date — label e.g. `01/09/24`
 * @property {number} creditPlus
 * @property {number} redeemedMinus
 * @property {number} change — signed (credit − redeemed)
 * @property {number} balance — running / column value
 */

/**
 * @typedef {object} CreditNoteFooterRow
 * @property {string} label — e.g. `Total`
 * @property {number} creditPlus
 * @property {number} redeemedMinus
 * @property {number} change
 * @property {number} balance
 */

/**
 * @typedef {object} CreditNoteChartModel
 * @property {CreditNoteSummary} summary
 * @property {CreditNoteDonutSlice} donut — two-slice donut (independent of daily series)
 * @property {string[]} categories — x labels (dates) for grouped bar
 * @property {number[]} creditPlus — bar series 1 (currency)
 * @property {number[]} redeemedMinus — bar series 2 (currency)
 * @property {CreditNoteTableRow[]} tableRows — body rows (excludes footer)
 * @property {CreditNoteFooterRow} footer
 */

/**
 * Map `api/v1/POS/dashboard/credit-note-graph` response to {@link CreditNoteChartModel} patch.
 * Expects `data.summary` (snake_case totals) and either:
 * - `data.points[]` with `x` (axis label / date), `credit_note`, `redeemed`, or
 * - `data.daily[]` with `date`, `credit_note`, `redeemed` (legacy).
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<CreditNoteChartModel>}
 */
export function mapCreditNoteGraphApiToModelPatch(apiResponse) {
  const d = apiResponse?.data ?? {};
  const s = d?.summary ?? {};
  const summary = {
    totalAmount: toNum(s?.total_amount, 0),
    creditNotePlus: toNum(s?.credit_note_plus, 0),
    redeemedMinus: toNum(s?.redeemed_minus, 0),
    changes: toNum(s?.changes, 0),
  };
  const donut = {
    creditPlus: summary.creditNotePlus,
    redeemedMinus: summary.redeemedMinus,
  };

  const rawRows =
    Array.isArray(d?.points) && d.points.length > 0
      ? d.points
      : Array.isArray(d?.daily)
        ? d.daily
        : [];
  const parsed = rawRows
    .map((row) => {
      const dateStr = String(row?.x ?? row?.date ?? "").trim();
      const m = moment(
        dateStr,
        ["DD/MM/YYYY", "D/M/YYYY", "DD/MM/YY", "D/M/YY", "YYYY-MM-DD"],
        true,
      );
      return {
        dateStr,
        m: m.isValid() ? m : null,
        credit: toNum(row?.credit_note, 0),
        redeemed: toNum(row?.redeemed, 0),
      };
    })
    .filter((x) => x.dateStr);

  parsed.sort((a, b) => {
    if (a.m && b.m) return a.m.valueOf() - b.m.valueOf();
    if (a.m) return -1;
    if (b.m) return 1;
    return String(a.dateStr).localeCompare(String(b.dateStr));
  });

  let running = 0;
  const tableRows = parsed.map((x) => {
    const creditPlus = x.credit;
    const redeemedMinus = x.redeemed;
    const change = creditPlus - redeemedMinus;
    running += change;
    return {
      date: x.dateStr,
      creditPlus,
      redeemedMinus,
      change,
      balance: running,
    };
  });

  const categories = tableRows.map((r) => r.date);
  const creditPlusArr = tableRows.map((r) => r.creditPlus);
  const redeemedMinusArr = tableRows.map((r) => r.redeemedMinus);

  const footer = {
    label: "Total",
    creditPlus: summary.creditNotePlus,
    redeemedMinus: summary.redeemedMinus,
    change: summary.changes,
    balance: summary.totalAmount,
  };

  return {
    summary,
    donut,
    categories,
    creditPlus: creditPlusArr,
    redeemedMinus: redeemedMinusArr,
    tableRows,
    footer,
  };
}

/** Demo credit note — Figma row between Saleperson performance and Custom order. */
export const STATIC_CREDIT_NOTE = {
  summary: {
    totalAmount: 1000000,
    creditNotePlus: 600000,
    redeemedMinus: 1300000,
    changes: -700000,
  },
  donut: {
    creditPlus: 300000,
    redeemedMinus: 700000,
  },
  categories: [
    "01/09/24",
    "02/09/24",
    "03/09/24",
    "04/09/24",
    "05/09/24",
    "06/09/24",
    "07/09/24",
    "08/09/24",
    "09/09/24",
    "10/09/24",
    "11/09/24",
    "12/09/24",
  ],
  creditPlus: [120000, 50000, 80000, 30000, 20000, 100000, 40000, 60000, 25000, 45000, 30000, 0],
  redeemedMinus: [40000, 70000, 60000, 90000, 75000, 50000, 85000, 55000, 95000, 40000, 80000, 60000],
  tableRows: [
    { date: "01/09/24", creditPlus: 120000, redeemedMinus: 40000, change: 80000, balance: 1080000 },
    { date: "02/09/24", creditPlus: 50000, redeemedMinus: 70000, change: -20000, balance: 1060000 },
    { date: "03/09/24", creditPlus: 80000, redeemedMinus: 60000, change: 20000, balance: 1080000 },
    { date: "04/09/24", creditPlus: 30000, redeemedMinus: 90000, change: -60000, balance: 1020000 },
    { date: "05/09/24", creditPlus: 20000, redeemedMinus: 75000, change: -55000, balance: 965000 },
    { date: "06/09/24", creditPlus: 100000, redeemedMinus: 50000, change: 50000, balance: 1015000 },
    { date: "07/09/24", creditPlus: 40000, redeemedMinus: 85000, change: -45000, balance: 970000 },
    { date: "08/09/24", creditPlus: 60000, redeemedMinus: 55000, change: 5000, balance: 975000 },
    { date: "09/09/24", creditPlus: 25000, redeemedMinus: 95000, change: -70000, balance: 905000 },
    { date: "10/09/24", creditPlus: 45000, redeemedMinus: 40000, change: 5000, balance: 910000 },
    { date: "11/09/24", creditPlus: 30000, redeemedMinus: 80000, change: -50000, balance: 860000 },
    { date: "12/09/24", creditPlus: 0, redeemedMinus: 60000, change: -560000, balance: 300000 },
  ],
  footer: {
    label: "Total",
    creditPlus: 600000,
    redeemedMinus: 1300000,
    change: -700000,
    balance: 300000,
  },
};

/** Live dashboard — no API data (not {@link STATIC_CREDIT_NOTE}). */
export const EMPTY_CREDIT_NOTE_MODEL = {
  summary: {
    totalAmount: 0,
    creditNotePlus: 0,
    redeemedMinus: 0,
    changes: 0,
  },
  donut: { creditPlus: 0, redeemedMinus: 0 },
  categories: [],
  creditPlus: [],
  redeemedMinus: [],
  tableRows: [],
  footer: {
    label: "Total",
    creditPlus: 0,
    redeemedMinus: 0,
    change: 0,
    balance: 0,
  },
};

/**
 * @param {Partial<CreditNoteChartModel> | null | undefined} patch
 * @returns {CreditNoteChartModel}
 */
export function mergeCreditNoteModel(patch) {
  const fb = STATIC_CREDIT_NOTE;
  const cloneRow = (r) => ({
    date: String(r?.date ?? ""),
    creditPlus: Number.isFinite(Number(r?.creditPlus)) ? Number(r.creditPlus) : 0,
    redeemedMinus: Number.isFinite(Number(r?.redeemedMinus)) ? Number(r.redeemedMinus) : 0,
    change: Number.isFinite(Number(r?.change))
      ? Number(r.change)
      : (Number(r?.creditPlus) || 0) - (Number(r?.redeemedMinus) || 0),
    balance: Number.isFinite(Number(r?.balance)) ? Number(r.balance) : 0,
  });

  if (!patch || typeof patch !== "object") {
    const e = EMPTY_CREDIT_NOTE_MODEL;
    return {
      summary: { ...e.summary },
      donut: { ...e.donut },
      categories: e.categories.slice(),
      creditPlus: e.creditPlus.slice(),
      redeemedMinus: e.redeemedMinus.slice(),
      tableRows: e.tableRows.map((x) => ({ ...x })),
      footer: { ...e.footer },
    };
  }

  const summaryIn = patch.summary && typeof patch.summary === "object" ? patch.summary : {};
  const donutIn = patch.donut && typeof patch.donut === "object" ? patch.donut : {};
  const categories = Array.isArray(patch.categories)
    ? patch.categories.map((x) => String(x ?? ""))
    : fb.categories.slice();
  const n = categories.length;

  const alignNums = (arr, fallbackArr) => {
    const src = Array.isArray(arr) ? arr : [];
    const out = [];
    for (let i = 0; i < n; i += 1) {
      const v = Number(src[i]);
      const fbv = Number(fallbackArr[Math.min(i, fallbackArr.length - 1)]) || 0;
      out.push(Number.isFinite(v) ? v : fbv);
    }
    return out;
  };

  let creditPlus = alignNums(patch.creditPlus, fb.creditPlus);
  let redeemedMinus = alignNums(patch.redeemedMinus, fb.redeemedMinus);

  let tableRows =
    Array.isArray(patch.tableRows) && patch.tableRows.length > 0
      ? patch.tableRows.map(cloneRow)
      : fb.tableRows.map((r, i) => {
          const c = creditPlus[i] ?? r.creditPlus;
          const rd = redeemedMinus[i] ?? r.redeemedMinus;
          return cloneRow({ ...r, creditPlus: c, redeemedMinus: rd, change: c - rd, balance: r.balance });
        });

  if (tableRows.length !== n) {
    tableRows = Array.from({ length: n }, (_, i) => {
      const b = fb.tableRows[Math.min(i, fb.tableRows.length - 1)];
      const c = creditPlus[i] ?? 0;
      const rd = redeemedMinus[i] ?? 0;
      return cloneRow({
        date: categories[i] || b.date,
        creditPlus: c,
        redeemedMinus: rd,
        change: c - rd,
        balance: Number(b.balance) || 0,
      });
    });
  }

  const footerIn = patch.footer && typeof patch.footer === "object" ? patch.footer : {};
  const footer = {
    label: String(footerIn.label ?? fb.footer.label),
    creditPlus: Number.isFinite(Number(footerIn.creditPlus)) ? Number(footerIn.creditPlus) : fb.footer.creditPlus,
    redeemedMinus: Number.isFinite(Number(footerIn.redeemedMinus)) ? Number(footerIn.redeemedMinus) : fb.footer.redeemedMinus,
    change: Number.isFinite(Number(footerIn.change)) ? Number(footerIn.change) : fb.footer.change,
    balance: Number.isFinite(Number(footerIn.balance)) ? Number(footerIn.balance) : fb.footer.balance,
  };

  return {
    summary: {
      totalAmount: Number.isFinite(Number(summaryIn.totalAmount)) ? Number(summaryIn.totalAmount) : fb.summary.totalAmount,
      creditNotePlus: Number.isFinite(Number(summaryIn.creditNotePlus)) ? Number(summaryIn.creditNotePlus) : fb.summary.creditNotePlus,
      redeemedMinus: Number.isFinite(Number(summaryIn.redeemedMinus)) ? Number(summaryIn.redeemedMinus) : fb.summary.redeemedMinus,
      changes: Number.isFinite(Number(summaryIn.changes)) ? Number(summaryIn.changes) : fb.summary.changes,
    },
    donut: {
      creditPlus: Number.isFinite(Number(donutIn.creditPlus)) ? Number(donutIn.creditPlus) : fb.donut.creditPlus,
      redeemedMinus: Number.isFinite(Number(donutIn.redeemedMinus)) ? Number(donutIn.redeemedMinus) : fb.donut.redeemedMinus,
    },
    categories,
    creditPlus,
    redeemedMinus,
    tableRows,
    footer,
  };
}

/**
 * @typedef {object} DebitNoteSummary
 * @property {number} totalValue — center + graph header total
 */

/**
 * @typedef {object} DebitNoteDonutSlice
 * @property {number} paid
 * @property {number} balance
 */

/**
 * @typedef {object} DebitNoteTableRow
 * @property {string} status — e.g. Paid / Balance
 * @property {number} amount
 * @property {number} pct — 0–100
 */

/**
 * Single breakdown (Installment or Layby).
 * @typedef {object} DebitNoteSliceModel
 * @property {DebitNoteSummary} summary
 * @property {DebitNoteDonutSlice} donut
 * @property {DebitNoteTableRow[]} tableRows
 */

/**
 * @typedef {object} DebitNoteChartModel
 * @property {DebitNoteSliceModel} installment
 * @property {DebitNoteSliceModel} layby
 */

/** Installment demo — Paid 75k / Balance 25k, total 100k (Figma). */
export const STATIC_DEBIT_NOTE_INSTALLMENT = {
  summary: { totalValue: 100000 },
  donut: { paid: 75000, balance: 25000 },
  tableRows: [
    { status: "Paid", amount: 75000, pct: 75 },
    { status: "Balance", amount: 25000, pct: 25 },
  ],
};

/** Layby demo — 50% / 50%, total 200k (Figma). */
export const STATIC_DEBIT_NOTE_LAYBY = {
  summary: { totalValue: 200000 },
  donut: { paid: 100000, balance: 100000 },
  tableRows: [
    { status: "Paid", amount: 100000, pct: 50 },
    { status: "Balance", amount: 100000, pct: 50 },
  ],
};

/** Live dashboard — missing slice (not {@link STATIC_DEBIT_NOTE_INSTALLMENT}). */
const EMPTY_DEBIT_NOTE_SLICE = {
  summary: { totalValue: 0 },
  donut: { paid: 0, balance: 0 },
  tableRows: [
    { status: "Paid", amount: 0, pct: 0 },
    { status: "Balance", amount: 0, pct: 0 },
  ],
};

/**
 * Map `api/v1/POS/dashboard/debit-note-graph` response to {@link DebitNoteChartModel} patch.
 * Expects `data.installment` / `data.layby` with `total`, `paid`, `balance`, `paid_percent`, `balance_percent`.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<DebitNoteChartModel>}
 */
export function mapDebitNoteGraphApiToModelPatch(apiResponse) {
  const d = apiResponse?.data ?? {};

  const mapSlice = (node) => {
    const n = node && typeof node === "object" ? node : {};
    const total = toNum(n.total, 0);
    const paid = toNum(n.paid, 0);
    const balance = toNum(n.balance, 0);
    const denom = paid + balance;
    const paidPctRaw = Number(n.paid_percent);
    const balPctRaw = Number(n.balance_percent);
    const paidPct = Number.isFinite(paidPctRaw)
      ? paidPctRaw
      : denom > 0
        ? (100 * paid) / denom
        : 0;
    const balPct = Number.isFinite(balPctRaw)
      ? balPctRaw
      : denom > 0
        ? (100 * balance) / denom
        : 0;

    return {
      summary: { totalValue: total },
      donut: { paid, balance },
      tableRows: [
        { status: "Paid", amount: paid, pct: paidPct },
        { status: "Balance", amount: balance, pct: balPct },
      ],
    };
  };

  return {
    installment: mapSlice(d.installment),
    layby: mapSlice(d.layby),
  };
}

/**
 * @param {Partial<DebitNoteSliceModel> | null | undefined} patch
 * @param {DebitNoteSliceModel} fb
 * @returns {DebitNoteSliceModel}
 */
function mergeDebitNoteSliceModel(patch, fb) {
  const p = patch && typeof patch === "object" ? patch : {};
  const summaryIn = p.summary && typeof p.summary === "object" ? p.summary : {};
  const donutIn = p.donut && typeof p.donut === "object" ? p.donut : {};

  const paid = Number.isFinite(Number(donutIn.paid)) ? Number(donutIn.paid) : fb.donut.paid;
  const balance = Number.isFinite(Number(donutIn.balance)) ? Number(donutIn.balance) : fb.donut.balance;
  const sumParts = paid + balance;
  const totalValue = Number.isFinite(Number(summaryIn.totalValue))
    ? Number(summaryIn.totalValue)
    : sumParts > 0
      ? sumParts
      : fb.summary.totalValue;

  const cloneRow = (r) => ({
    status: String(r?.status ?? ""),
    amount: Number.isFinite(Number(r?.amount)) ? Number(r.amount) : 0,
    pct: Number.isFinite(Number(r?.pct)) ? Number(r.pct) : 0,
  });

  let tableRows;
  if (Array.isArray(p.tableRows) && p.tableRows.length > 0) {
    tableRows = p.tableRows.map(cloneRow);
  } else {
    const denom = paid + balance;
    tableRows = [
      { status: "Paid", amount: paid, pct: denom > 0 ? Math.round((100 * paid) / denom) : 0 },
      { status: "Balance", amount: balance, pct: denom > 0 ? Math.round((100 * balance) / denom) : 0 },
    ];
  }

  return {
    summary: { totalValue },
    donut: { paid, balance },
    tableRows,
  };
}

/**
 * @param {Partial<DebitNoteChartModel> | null | undefined} patch
 * @returns {DebitNoteChartModel}
 */
export function mergeDebitNoteModel(patch) {
  const p = patch && typeof patch === "object" ? patch : {};
  const instIn = p.installment && typeof p.installment === "object" ? p.installment : null;
  const laybyIn = p.layby && typeof p.layby === "object" ? p.layby : null;
  return {
    installment: mergeDebitNoteSliceModel(instIn, EMPTY_DEBIT_NOTE_SLICE),
    layby: mergeDebitNoteSliceModel(laybyIn, EMPTY_DEBIT_NOTE_SLICE),
  };
}

/**
 * Maximum points on Sale overview x-axis. Shorter spans (≤15 days) use one category per calendar day (≤15 points).
 * Longer spans split the selected range evenly into exactly this many contiguous day buckets covering all days.
 */
export const SALE_OVERVIEW_MAX_AXIS_POINTS = 15;

/** @deprecated Alias — prefer {@link SALE_OVERVIEW_MAX_AXIS_POINTS}. */
export const SALE_OVERVIEW_MAX_BUCKETS = SALE_OVERVIEW_MAX_AXIS_POINTS;

/**
 * Builds x-axis labels spanning [start,end] inclusively (max {@link SALE_OVERVIEW_MAX_AXIS_POINTS} categories).
 * — Span ≤ {@link SALE_OVERVIEW_MAX_AXIS_POINTS}: one label per calendar day (every day appears; ≤15 ticks).
 * — Longer spans: exactly {@link SALE_OVERVIEW_MAX_AXIS_POINTS} contiguous day buckets partitioning the interval;
 *   label = bucket start date (full timeframe is preserved).
 *
 * @param {Date | null | undefined} start
 * @param {Date | null | undefined} end
 * @param {number} [maxPoints=15]
 * @returns {{ labels: string[], dayCount: number, bucketDayCounts: number[] }}
 */
export function saleOverviewTimeBucketsFromDashboardRange(
  start,
  end,
  maxPoints = SALE_OVERVIEW_MAX_AXIS_POINTS,
) {
  const fmt = (d) => moment(d).format("DD/MM/YY");
  if (!start || !end) {
    return {
      labels: [],
      dayCount: 0,
      bucketDayCounts: [],
    };
  }
  const s = moment(start).startOf("day");
  const e = moment(end).startOf("day");
  const n = Math.max(1, e.diff(s, "days") + 1);

  if (n <= maxPoints) {
    const labels = [];
    const bucketDayCounts = [];
    for (let i = 0; i < n; i += 1) {
      labels.push(fmt(moment(s).add(i, "days")));
      bucketDayCounts.push(1);
    }
    return { labels, dayCount: n, bucketDayCounts };
  }

  const bucketCount = maxPoints;
  const labels = [];
  const bucketDayCounts = [];
  for (let b = 0; b < bucketCount; b += 1) {
    const bucketStartDay = Math.floor((b * n) / bucketCount);
    labels.push(fmt(moment(s).add(bucketStartDay, "days")));
    const nextStartDay = Math.floor(((b + 1) * n) / bucketCount);
    bucketDayCounts.push(Math.max(1, nextStartDay - bucketStartDay));
  }

  return { labels, dayCount: n, bucketDayCounts };
}

/**
 * Full Sale overview chart model aligned to dashboard dates (zeros until API provides series).
 * With long ranges the x-axis has at most {@link SALE_OVERVIEW_MAX_AXIS_POINTS} categories (see {@link saleOverviewTimeBucketsFromDashboardRange}).
 *
 * @param {Date | null | undefined} start
 * @param {Date | null | undefined} end
 * @param {Partial<SaleOverviewChartModel> | null | undefined} patch
 */
export function saleOverviewModelFromDashboardRange(start, end, patch = null) {
  const ownsLabels = patch?.labels?.length > 0;
  if (ownsLabels) {
    return mergeSaleOverviewChartModel(patch);
  }
  const partial = patch && typeof patch === "object" ? patch : {};
  const { labels } = saleOverviewTimeBucketsFromDashboardRange(start, end);
  const len = labels.length;

  const seriesAlignedToBuckets =
    Array.isArray(partial.amount) &&
    partial.amount.length === len &&
    Array.isArray(partial.qty) &&
    partial.qty.length === len &&
    Array.isArray(partial.customerCount) &&
    partial.customerCount.length === len;

  if (seriesAlignedToBuckets) {
    return mergeSaleOverviewChartModel({ ...partial, labels });
  }

  const zeros = Array(len).fill(0);
  return mergeSaleOverviewChartModel({
    ...partial,
    labels,
    amount: zeros,
    qty: zeros,
    customerCount: zeros,
  });
}

/**
 * @deprecated Prefer {@link saleOverviewTimeBucketsFromDashboardRange} or {@link saleOverviewModelFromDashboardRange}.
 */
export function saleOverviewLabelsFromDashboardRange(start, end) {
  return saleOverviewTimeBucketsFromDashboardRange(start, end).labels;
}
/**
 * @param {Partial<SaleOverviewChartModel> | null | undefined} patch
 * @param {SaleOverviewChartModel} [base=EMPTY_SALE_OVERVIEW_CHART]
 * @returns {SaleOverviewChartModel}
 */
export function mergeSaleOverviewChartModel(patch, base = EMPTY_SALE_OVERVIEW_CHART) {
  const fallback = {
    ...base,
    labels: [...base.labels],
    amount: [...base.amount],
    qty: [...base.qty],
    customerCount: [...base.customerCount],
  };
  if (!patch || typeof patch !== "object") return fallback;
  const labels = Array.isArray(patch.labels) && patch.labels.length ? [...patch.labels] : fallback.labels;
  const len = labels.length;
  const alignNums = (arr, fb) => {
    const src = Array.isArray(arr) ? [...arr] : [...fb];
    if (src.length >= len) return src.slice(0, len);
    while (src.length < len) src.push(src[src.length - 1] ?? 0);
    return src;
  };
  return {
    labels,
    amount: alignNums(patch.amount, fallback.amount),
    qty: alignNums(patch.qty, fallback.qty),
    customerCount: alignNums(patch.customerCount, fallback.customerCount),
    leftAxisLabel: patch.leftAxisLabel ?? fallback.leftAxisLabel,
    rightAxisLabel: patch.rightAxisLabel ?? fallback.rightAxisLabel,
  };
}

const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Map `api/v1/POS/dashboard/sale-overview-graph` response to SaleOverviewChartModel patch.
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<SaleOverviewChartModel>}
 */
export function mapSaleOverviewApiToModelPatch(apiResponse) {
  const src = apiResponse?.sale_overview ?? {};
  const points = Array.isArray(src?.points) ? src.points : [];
  return {
    labels: points.map((p) => String(p?.x ?? "")),
    amount: points.map((p) => toNum(p?.amount, 0)),
    qty: points.map((p) => toNum(p?.qty, 0)),
    customerCount: points.map((p) => toNum(p?.customer, 0)),
    leftAxisLabel: String(src?.y_left?.label ?? "Amount"),
    rightAxisLabel: String(src?.y_right?.label ?? "Value"),
  };
}

/**
 * Map `api/v1/POS/dashboard/total-sale-graph` response to {@link TotalSaleGroupedModel} patch.
 * `by_amounts` / `by_qty` rows use `label` plus `sale` / `exchange` / `refund` keyed to the series.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<TotalSaleGroupedModel>}
 */
export function mapTotalSaleGraphApiToModelPatch(apiResponse) {
  const root = apiResponse?.total_sale ?? {};
  const amountRows = Array.isArray(root?.by_amounts?.data) ? root.by_amounts.data : [];
  const qtyRows = Array.isArray(root?.by_qty?.data) ? root.by_qty.data : [];

  /** @type {Map<string, { sale?: unknown; exchange?: unknown; refund?: unknown }>} */
  const qtyByLabel = new Map();
  for (const row of qtyRows) {
    const key = String(row?.label ?? "").trim().toLowerCase();
    if (key) qtyByLabel.set(key, row);
  }

  const categories = [];
  const sale = [];
  const exchange = [];
  const refund = [];
  const saleQty = [];
  const exchangeQty = [];
  const refundQty = [];

  for (const row of amountRows) {
    const label = String(row?.label ?? "");
    categories.push(label);
    sale.push(toNum(row?.sale, 0));
    exchange.push(toNum(row?.exchange, 0));
    refund.push(toNum(row?.refund, 0));
    const q = qtyByLabel.get(label.trim().toLowerCase());
    saleQty.push(toNum(q?.sale, 0));
    exchangeQty.push(toNum(q?.exchange, 0));
    refundQty.push(toNum(q?.refund, 0));
  }

  if (!categories.length && qtyRows.length) {
    for (const row of qtyRows) {
      const label = String(row?.label ?? "");
      categories.push(label);
      sale.push(0);
      exchange.push(0);
      refund.push(0);
      saleQty.push(toNum(row?.sale, 0));
      exchangeQty.push(toNum(row?.exchange, 0));
      refundQty.push(toNum(row?.refund, 0));
    }
  }

  const collections = categories.map(() => "");

  return {
    categories,
    collections,
    sale,
    exchange,
    refund,
    saleQty,
    exchangeQty,
    refundQty,
  };
}

/** Keys under `segment.<type>.by_item` / `by_collection` — matches Segment card sales-type filter. */
export const SEGMENT_GRAPH_SALES_KEYS = ["total_sales", "custom", "repair", "exchange", "refund"];

/**
 * One sales-type slice for one axis from `segment-graph` API.
 *
 * @typedef {object} SegmentGraphAxisRows
 * @property {string[]} labels
 * @property {number[]} amount
 * @property {number[]} qty
 * @property {number} amountTotal
 * @property {number} qtyTotal
 */

/**
 * @typedef {Object.<string, SegmentGraphAxisRows>} SegmentGraphByAxis
 */

/**
 * @typedef {object} SegmentGraphModel
 * @property {SegmentGraphByAxis} byItem
 * @property {SegmentGraphByAxis} byCollection
 */

/**
 * @param {object | null | undefined} block — `by_item` / `by_collection` block (`data`, `amount_total`, `qty_total`).
 * @returns {SegmentGraphAxisRows}
 */
function mapSegmentGraphAxisBlock(block) {
  const rows = Array.isArray(block?.data) ? block.data : [];
  return {
    labels: rows.map((r) => String(r?.label ?? "")),
    amount: rows.map((r) => toNum(r?.amount, 0)),
    qty: rows.map((r) => toNum(r?.qty, 0)),
    amountTotal: toNum(block?.amount_total, 0),
    qtyTotal: toNum(block?.qty_total, 0),
  };
}

/**
 * @param {object | null | undefined} segmentRoot — `apiResponse.segment`
 * @param {"by_item" | "by_collection"} axisKey
 * @returns {SegmentGraphByAxis}
 */
function buildSegmentGraphByAxisFromApi(segmentRoot, axisKey) {
  /** @type {SegmentGraphByAxis} */
  const out = {};
  for (const k of SEGMENT_GRAPH_SALES_KEYS) {
    out[k] = mapSegmentGraphAxisBlock(segmentRoot?.[k]?.[axisKey]);
  }
  return out;
}

/**
 * Map `api/v1/POS/dashboard/segment-graph` response to {@link SegmentGraphModel}.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {SegmentGraphModel}
 */
export function mapSegmentGraphApiToModel(apiResponse) {
  const segment = apiResponse?.segment ?? {};
  return {
    byItem: buildSegmentGraphByAxisFromApi(segment, "by_item"),
    byCollection: buildSegmentGraphByAxisFromApi(segment, "by_collection"),
  };
}

/**
 * @param {SegmentGraphModel | null | undefined} m
 * @param {string} axisId — `item` | `collection`
 * @param {string} salesTypeId
 * @returns {SegmentGraphAxisRows | null}
 */
export function getSegmentGraphSlice(m, axisId, salesTypeId) {
  if (!m || typeof m !== "object") return null;
  const side = axisId === "collection" ? m.byCollection : m.byItem;
  if (!side || typeof side !== "object") return null;
  const slice = side[salesTypeId];
  if (!slice || !Array.isArray(slice.labels) || slice.labels.length < 1) return null;
  return slice;
}

/**
 * Map `api/v1/POS/dashboard/wishlist-graph` response to {@link CustomOrderChartModel} patch (Wishlist card).
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<CustomOrderChartModel>}
 */
export function mapWishlistGraphApiToModelPatch(apiResponse) {
  const rows = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
  return {
    categories: rows.map((r) => String(r?.item_name ?? "")),
    amount: rows.map((r) => toNum(r?.amount, 0)),
    qty: rows.map((r) => toNum(r?.qty, 0)),
    customer: rows.map((r) => toNum(r?.customer, 0)),
  };
}

/**
 * Map `api/v1/POS/dashboard/custom-order-graph` response to {@link CustomOrderChartModel} patch.
 * Expects `data[]` with `label` (or `item_name` / `category`), `amount`, `qty`, `customer`.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<CustomOrderChartModel>}
 */
export function mapCustomOrderGraphApiToModelPatch(apiResponse) {
  const rows = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
  return {
    categories: rows.map((r) => String(r?.label ?? r?.item_name ?? r?.category ?? "")),
    amount: rows.map((r) => toNum(r?.amount, 0)),
    qty: rows.map((r) => toNum(r?.qty, 0)),
    customer: rows.map((r) => toNum(r?.customer, 0)),
  };
}

/**
 * Map `api/v1/POS/dashboard/repair-order-graph` response to {@link CustomOrderChartModel} patch (Repair Order card).
 * Same `data[]` row shape as {@link mapCustomOrderGraphApiToModelPatch}.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<CustomOrderChartModel>}
 */
export function mapRepairOrderGraphApiToModelPatch(apiResponse) {
  return mapCustomOrderGraphApiToModelPatch(apiResponse);
}

/**
 * Map `api/v1/POS/dashboard/exchange-graph` response to {@link CustomOrderChartModel} patch (Exchange card).
 * Same `data[]` row shape as {@link mapCustomOrderGraphApiToModelPatch}.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<CustomOrderChartModel>}
 */
export function mapExchangeGraphApiToModelPatch(apiResponse) {
  return mapCustomOrderGraphApiToModelPatch(apiResponse);
}

/**
 * Map `api/v1/POS/dashboard/refund-graph` response to {@link CustomOrderChartModel} patch (Refund card).
 * Same `data[]` row shape as {@link mapCustomOrderGraphApiToModelPatch}.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<CustomOrderChartModel>}
 */
export function mapRefundGraphApiToModelPatch(apiResponse) {
  return mapCustomOrderGraphApiToModelPatch(apiResponse);
}

/**
 * Map `api/v1/POS/dashboard/reserved-graph` response to {@link CustomOrderChartModel} patch (Reserved card).
 * Same `data[]` row shape as {@link mapCustomOrderGraphApiToModelPatch}.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<CustomOrderChartModel>}
 */
export function mapReservedGraphApiToModelPatch(apiResponse) {
  return mapCustomOrderGraphApiToModelPatch(apiResponse);
}

/**
 * @typedef {object} CashRegisterExpectedActualModel
 * @property {string[]} labels — x-axis date labels
 * @property {number[]} expected — THB per label
 * @property {number[]} actual — THB per label
 */

/** Demo cash register expected vs actual (Figma) — until `cash-register-graph` loads. */
export const STATIC_CASH_REGISTER_EXPECTED_ACTUAL = {
  labels: ["01/09/24", "02/09/24", "03/09/24", "04/09/24", "05/09/24", "06/09/24", "07/09/24", "08/09/24", "09/09/24", "10/09/24", "11/09/24", "12/09/24"],
  expected: [13000, 52000, 50000, 64000, 64000, 64000, 70000, 64000, 64000, 81000, 64000, 70000],
  actual: [12000, 43000, 52000, 63000, 63000, 63000, 80000, 50000, 59000, 70000, 63000, 63000],
};

/** Live dashboard — no API data (not {@link STATIC_CASH_REGISTER_EXPECTED_ACTUAL}). */
export const EMPTY_CASH_REGISTER_EXPECTED_ACTUAL = {
  labels: [],
  expected: [],
  actual: [],
};

/**
 * @param {Partial<CashRegisterExpectedActualModel> | null | undefined} patch
 * @param {CashRegisterExpectedActualModel} [base=EMPTY_CASH_REGISTER_EXPECTED_ACTUAL]
 * @returns {CashRegisterExpectedActualModel}
 */
export function mergeCashRegisterExpectedActualModel(
  patch,
  base = EMPTY_CASH_REGISTER_EXPECTED_ACTUAL,
) {
  const fb = {
    labels: [...base.labels],
    expected: [...base.expected],
    actual: [...base.actual],
  };
  if (!patch || typeof patch !== "object") return fb;
  if (Array.isArray(patch.labels) && patch.labels.length === 0) {
    return { labels: [], expected: [], actual: [] };
  }
  if (!Array.isArray(patch.labels) || patch.labels.length === 0) {
    return { labels: [], expected: [], actual: [] };
  }

  const labels = patch.labels.map((x) => (x == null ? "" : String(x)));
  const n = labels.length;
  const align = (arr, fbArr) => {
    const src = Array.isArray(arr) ? arr : [];
    const pad = Array.isArray(fbArr) ? fbArr : [];
    const out = [];
    for (let i = 0; i < n; i += 1) {
      const v = Number(src[i]);
      const fbI = pad[Math.min(i, Math.max(0, pad.length - 1))];
      out.push(Number.isFinite(v) ? v : Number(fbI) || 0);
    }
    return out;
  };
  return {
    labels,
    expected: align(patch.expected, fb.expected),
    actual: align(patch.actual, fb.actual),
  };
}

/**
 * Map `api/v1/POS/dashboard/cash-register-graph` response to {@link CashRegisterExpectedActualModel} patch.
 * Expects `data[]` with `date` (or `x`), `expected`, `actual` (THB).
 *
 * @param {object | null | undefined} apiResponse
 * @returns {Partial<CashRegisterExpectedActualModel>}
 */
export function mapCashRegisterGraphApiToModelPatch(apiResponse) {
  const rows = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
  const parsed = rows
    .map((row) => {
      const dateStr = String(row?.date ?? row?.x ?? "").trim();
      const m = moment(
        dateStr,
        ["DD/MM/YYYY", "D/M/YYYY", "DD/MM/YY", "D/M/YY", "YYYY-MM-DD"],
        true,
      );
      return {
        dateStr,
        m: m.isValid() ? m : null,
        expected: toNum(row?.expected, 0),
        actual: toNum(row?.actual, 0),
      };
    })
    .filter((x) => x.dateStr);

  parsed.sort((a, b) => {
    if (a.m && b.m) return a.m.valueOf() - b.m.valueOf();
    if (a.m) return -1;
    if (b.m) return 1;
    return String(a.dateStr).localeCompare(String(b.dateStr));
  });

  return {
    labels: parsed.map((x) => x.dateStr),
    expected: parsed.map((x) => x.expected),
    actual: parsed.map((x) => x.actual),
  };
}

/**
 * @typedef {object} TopProductTableRow
 * @property {string} sku
 * @property {string} name
 * @property {string} item
 * @property {string} collection
 * @property {number} qty
 * @property {number} qtySharePct
 * @property {number} amount
 * @property {string} [mainImage] — product thumbnail URL when provided by API
 * @property {number} [rank] — server rank (optional; table index used after client sort)
 */

/**
 * Map `api/v1/POS/dashboard/top-products` `data[]` to rows for {@link TopProductsChartCard}.
 *
 * @param {object | null | undefined} apiResponse
 * @returns {TopProductTableRow[]}
 */
export function mapTopProductsApiToRows(apiResponse) {
  const rows = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
  return rows.map((r, idx) => ({
    sku: String(r?.SKU ?? r?.sku ?? ""),
    name: String(r?.name ?? ""),
    item: String(r?.item ?? ""),
    collection: String(r?.collection ?? ""),
    qty: toNum(r?.qty, 0),
    qtySharePct: toNum(r?.qty_percent, 0),
    amount: toNum(r?.amount, 0),
    mainImage:
      typeof r?.main_image === "string" && String(r.main_image).trim() ? String(r.main_image).trim() : "",
    rank: toNum(r?.rank, idx + 1),
  }));
}

/**
 * Per-category values for Segment card (donut / bar / table) from {@link TotalSaleGroupedModel}.
 *
 * @param {TotalSaleGroupedModel | undefined} model
 * @param {string} salesTypeId — `total_sales` | `custom` | `repair` | `exchange` | `refund`
 * @param {boolean} useQty — true when segment UI is “By Qty”
 * @returns {number[]}
 */
export function buildSegmentValueSeries(model, salesTypeId, useQty) {
  const categories = model?.categories ?? [];
  const len = categories.length;
  if (len < 1) return [];

  const pull = (/** @type {keyof TotalSaleGroupedModel} */ key) => {
    const arr = model?.[key];
    return Array.isArray(arr) ? arr : [];
  };
  const alignLen = (arr) => {
    const a = arr.map((x) => (Number.isFinite(Number(x)) ? Number(x) : 0));
    while (a.length < len) a.push(0);
    return a.slice(0, len);
  };

  const sale = alignLen(pull("sale"));
  const exchange = alignLen(pull("exchange"));
  const refund = alignLen(pull("refund"));
  const custom = alignLen(pull("custom"));
  const repair = alignLen(pull("repair"));
  const saleQ = alignLen(pull("saleQty"));
  const exchangeQ = alignLen(pull("exchangeQty"));
  const refundQ = alignLen(pull("refundQty"));
  const customQ = alignLen(pull("customQty"));
  const repairQ = alignLen(pull("repairQty"));

  const out = [];
  for (let i = 0; i < len; i += 1) {
    if (useQty) {
      switch (salesTypeId) {
        case "custom":
          out.push(customQ[i]);
          break;
        case "repair":
          out.push(repairQ[i]);
          break;
        case "exchange":
          out.push(exchangeQ[i]);
          break;
        case "refund":
          out.push(refundQ[i]);
          break;
        case "total_sales":
        default:
          out.push(saleQ[i] + exchangeQ[i] + refundQ[i] + customQ[i] + repairQ[i]);
      }
    } else {
      switch (salesTypeId) {
        case "custom":
          out.push(custom[i]);
          break;
        case "repair":
          out.push(repair[i]);
          break;
        case "exchange":
          out.push(exchange[i]);
          break;
        case "refund":
          out.push(refund[i]);
          break;
        case "total_sales":
        default:
          out.push(sale[i] + exchange[i] + refund[i] + custom[i] + repair[i]);
      }
    }
  }
  return out;
}

/**
 * @param {Partial<TotalSaleGroupedModel> | null | undefined} patch
 * @param {TotalSaleGroupedModel} [base=EMPTY_TOTAL_SALE_GROUPED_MODEL]
 * @returns {TotalSaleGroupedModel}
 */
export function mergeTotalSaleGroupedModel(patch, base = EMPTY_TOTAL_SALE_GROUPED_MODEL) {
  const fallback = {
    ...base,
    categories: [...base.categories],
    collections: Array.isArray(base.collections) ? [...base.collections] : [],
    sale: [...base.sale],
    exchange: [...base.exchange],
    refund: [...base.refund],
    saleQty: [...(base.saleQty ?? [])],
    exchangeQty: [...(base.exchangeQty ?? [])],
    refundQty: [...(base.refundQty ?? [])],
    custom: [...(base.custom ?? [])],
    repair: [...(base.repair ?? [])],
    customQty: [...(base.customQty ?? [])],
    repairQty: [...(base.repairQty ?? [])],
  };
  if (!patch || typeof patch !== "object") return fallback;
  const categories = Array.isArray(patch.categories) ? [...patch.categories] : fallback.categories;
  const len = categories.length;
  const align = (arr, fb) => {
    const src = Array.isArray(arr) ? [...arr] : [...fb];
    if (src.length >= len) return src.slice(0, len);
    while (src.length < len) src.push(src[src.length - 1] ?? 0);
    return src;
  };
  const alignStr = (arr, fb) => {
    const srcRaw = Array.isArray(arr) && arr.length > 0 ? arr : fb;
    const src = (Array.isArray(srcRaw) ? srcRaw : []).map((x) => (x == null ? "" : String(x)));
    if (src.length >= len) return src.slice(0, len);
    const out = [...src];
    const pad = out.length > 0 ? out[out.length - 1] : "";
    while (out.length < len) out.push(pad);
    return out;
  };
  return {
    categories,
    collections: alignStr(patch.collections, fallback.collections),
    sale: align(patch.sale, fallback.sale),
    exchange: align(patch.exchange, fallback.exchange),
    refund: align(patch.refund, fallback.refund),
    saleQty: align(patch.saleQty, fallback.saleQty),
    exchangeQty: align(patch.exchangeQty, fallback.exchangeQty),
    refundQty: align(patch.refundQty, fallback.refundQty),
    custom: align(patch.custom, fallback.custom),
    repair: align(patch.repair, fallback.repair),
    customQty: align(patch.customQty, fallback.customQty),
    repairQty: align(patch.repairQty, fallback.repairQty),
    groupBy: patch.groupBy ?? fallback.groupBy,
  };
}
